/**
 * Área "Compromissos": o calendário semanal e o modal de compromisso. Porte do admin atual
 * (`portal/admin.html:4326-4706`, modal em `2840-2903`).
 *
 * O QUE A PESSOA FAZ AQUI, igual ao legado: vê a semana de domingo a sábado, das 07:00 às 22:00;
 * anda de semana em semana ("Anterior", "Hoje", "Seguinte"); clica num horário vazio para marcar
 * ("Novo compromisso" já com o dia e a hora) ou num evento para editar; abre o WhatsApp do lead ou
 * a ficha da cliente direto do evento. O modal valida, grava, zera a resposta da cliente quando o
 * horário muda, e pede o e-mail de confirmação quando cabe. A SEMANA ESCOLHIDA fica se ela sair
 * para outra área e voltar, como fica no legado (ver o bloco sobre isso abaixo).
 *
 * O RECORTE POR PAPEL (`admin.html:3367-3397`): o menu só mostra esta área a administrador e
 * consulta. Para consulta, "+ Novo compromisso" some, o modal abre com os campos travados, sem
 * "Salvar", "Excluir", "Cancelar" nem × (achado 5), e sai só pelo ESC.
 *
 * O MODAL COM CAMPO é o `ModalLegado` comum, onde mora a única exceção do lint do Chassi (a
 * migração reproduz o modal do legado até o fundador decidir). As setas "←", "→" e "↗" do legado
 * viram desenho do Chassi, porque símbolo Unicode não entra na tela.
 */

// Estado, referência e efeito do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// As peças do Chassi: botão, campos e o desenho da seta.
import { Botao, Campo, CampoTexto, IconeVoltar } from '@complexity/ui';
// O dicionário: a frase em português é a chave.
import { t } from '@complexity/i18n';
// A área de dados: as sete chamadas do legado.
import {
  atualizarCompromisso,
  criarCompromisso,
  excluirCompromisso,
  lerLeadDoCompromisso,
  listarCompromissosDoPeriodo,
  listarLeadsParaCompromisso,
  notificarConfirmacaoCompromisso,
  type CompromissoDaSemana,
  type LeadParaCompromisso,
} from '@studiolu/dados/areas/compromissos';
// Sessão, papel, fonte de dados e navegação do painel.
import { usePainel } from '../../contexto.js';
// O endereço da ficha com o atendimento e a origem do "Voltar": o contrato da ficha (frente A2),
// numa função só, para quem monta e quem lê não divergirem.
import { enderecoDaFicha } from '../ficha-cliente/comum-ficha/volta.js';
// As regras do calendário, sem tela.
import {
  CAL_ALTURA_HORA,
  CAL_DIAS_SEMANA,
  LARGURA_COLUNA_HORAS,
  LARGURA_MIN_COLUNA,
  NUM_DIAS,
  OPCOES_AVISO,
  OPCOES_DURACAO,
  camposDaEdicao,
  camposNovos,
  eventosDoDia,
  fimDoPeriodo,
  formatarStatusConfirmacao,
  horasDaGrade,
  inicioDoPeriodo,
  leadNoCampo,
  montarRegistro,
  rotuloDoIntervalo,
  rotuloDoLead,
  somarDias,
  validarCompromisso,
  type CamposDoCompromisso,
  type EventoNaGrade,
} from './calendario.js';
// A confirmação e o aviso do legado, peça comum (o achado 5 mora dentro dela).
import { useConfirmacao } from '../../comum/confirmacao.js';
// O modal do legado, peça comum: ×, ESC, clique fora opcional e o achado 5 num ponto só.
import { ModalLegado } from '../../comum/modal-legado.js';
// O ícone do WhatsApp Web do legado, peça comum.
import { LinkWhatsapp } from '../../comum/whatsapp.js';
// O campo de escolha do modal.
import { Escolha } from './Escolha.js';
// O "Excluir" vermelho, e o defeito do legado que o reexibe para consulta, isolado num arquivo.
import { CLASSE_DE_EXCLUIR, ConfirmacaoSoComCancelar, ExcluirDeConsulta, LEGADO_REEXIBE_EXCLUIR_PARA_CONSULTA } from './consulta.js';

// A semana na tela: nada antes da primeira leitura (o legado começa em branco), o erro, ou os dados.
type EstadoDaSemana =
  | { readonly tipo: 'nada' }
  | { readonly tipo: 'erro'; readonly mensagem: string }
  | { readonly tipo: 'pronta'; readonly inicio: Date; readonly compromissos: readonly CompromissoDaSemana[] };

// O modal aberto: o compromisso em edição (ou `null` no novo), os campos, a resposta da cliente e
// o campo "Lead" em branco (o lead do compromisso fora da lista, `leadNoCampo`).
interface EstadoDoModal {
  readonly emEdicao: CompromissoDaSemana | null;
  readonly campos: CamposDoCompromisso;
  readonly statusConfirmacao: string;
  readonly leadEmBranco: boolean;
}

/*
 * O QUE SOBREVIVE À TROCA DE ÁREA, como no legado (linha 147 do inventário).
 *
 * No legado a área é uma aba da mesma página: `periodoInicio` é uma variável da página
 * (`admin.html:4360`), calculada uma vez quando ela carrega, e o clique na aba só relê a semana
 * guardada (`carregarCalendario()`, `6448`), sem voltar para hoje. A grade antiga continua no
 * documento até a leitura nova chegar (`carregarCalendario` só troca o `innerHTML` depois do
 * `await`, `4375-4393`). Nem o "Sair" apaga nada disso, porque ele não recarrega a página (`3358`).
 *
 * Aqui a tela desmonta ao trocar de rota (`rotas.tsx`, `AreaUtil`), então as duas coisas moram fora
 * dela, no módulo: ele é importado direto pelas rotas e avalia uma vez por carga da página, que é o
 * mesmo tempo de vida da variável do legado. "Hoje" continua sendo o único caminho de volta.
 */
let inicioDaPagina = inicioDoPeriodo(new Date());
let semanaDaPagina: EstadoDaSemana = { tipo: 'nada' };

// DEFEITO DO LEGADO REPRODUZIDO, isolado aqui (decisão do fundador: defeito do legado se porta
// igual, num ponto só). O legado lê os leads do campo "Lead" UMA vez por login (a chamada só existe
// no pós-login, `admin.html:3513`) e nunca mais: lead criado depois só aparece no campo saindo e
// entrando de novo, ou recarregando a página. Aqui a leitura fica guardada por sessão, e o app só
// troca o objeto da sessão no login (`App.tsx:50-54`), então vale o mesmo "uma vez por login". A
// falha também fica guardada, porque o legado não tenta de novo. O que resta de diferente é só o
// momento da leitura: lá, no login; aqui, na primeira abertura desta área depois dele.
//
// O resultado fica guardado junto da promessa: `undefined` enquanto ela não voltou, `null` se
// falhou, e a lista. É ele que a tela lê de cara ao voltar à área, sem esperar outra volta do laço.
interface LeituraDosLeads {
  readonly promessa: Promise<readonly LeadParaCompromisso[] | null>;
  resultado: readonly LeadParaCompromisso[] | null | undefined;
}
const leadsDaSessao = new WeakMap<object, LeituraDosLeads>();

// A leitura da sessão, começada na primeira chamada e reaproveitada nas seguintes.
function leituraDosLeads(sessao: object, dados: Parameters<typeof listarLeadsParaCompromisso>[0]): LeituraDosLeads {
  const guardada = leadsDaSessao.get(sessao);
  if (guardada) return guardada;
  const nova: LeituraDosLeads = {
    // A falha vira `null`, e só fica no registro, como o `console.error` do legado (`admin.html:4366`).
    promessa: listarLeadsParaCompromisso(dados).then((r) => {
      nova.resultado = r.ok ? r.dados : null;
      return nova.resultado;
    }),
    resultado: undefined,
  };
  leadsDaSessao.set(sessao, nova);
  return nova;
}

// A cor de cada tipo de evento, pelos tokens: com lead é a cor de ação, manual é o cinza, e a
// resposta da cliente pinta de sucesso ou de erro (`.cal-evento`, `admin.html:1020-1028`).
const COR_DO_EVENTO: Readonly<Record<EventoNaGrade['tom'], string>> = {
  lead: 'bg-marca text-marca-contraste',
  manual: 'bg-texto-suave text-superficie',
  confirmado: 'bg-sucesso-suave text-sucesso',
  recusado: 'bg-erro-suave text-erro',
};

export function TelaCompromissos() {
  const { dados, acesso, navegar, sessao } = usePainel();
  // Consulta vê tudo e não muda nada.
  const somenteLeitura = acesso.papel === 'consulta';
  // A confirmação de excluir e os avisos, sem botão para consulta (achado 5, dentro da peça).
  const { confirmar, alertar, dialogo } = useConfirmacao();
  // O domingo da semana mostrada: o guardado na página, que nasce na semana de hoje.
  const [inicio, setInicioNaTela] = useState(() => inicioDaPagina);
  // A semana lida do banco; ao voltar à área, a última lida continua na tela até a nova chegar.
  const [semana, setSemanaNaTela] = useState<EstadoDaSemana>(() => semanaDaPagina);
  // Somado a cada gravação, para reler a MESMA semana, como `await carregarCalendario()`.
  const [recarga, setRecarga] = useState(0);
  // Os leads do campo "Lead": `undefined` enquanto a leitura não voltou, `null` se ela falhou, ou a
  // lista. Ao voltar à área, a leitura da sessão já tem o resultado, e ele vale de cara.
  const [leads, setLeads] = useState<readonly LeadParaCompromisso[] | null | undefined>(() => leadsDaSessao.get(sessao)?.resultado);
  // O modal, ou `null` fechado.
  const [modal, setModal] = useState<EstadoDoModal | null>(null);
  // A linha de status do modal: validação, "Salvando..." e erro de gravação.
  const [statusModal, setStatusModal] = useState('');
  // A confirmação de consulta, só com "Cancelar" (o defeito do legado em `consulta.tsx`).
  const [confirmandoConsulta, setConfirmandoConsulta] = useState(false);
  // O cabeçalho do dia de hoje, para centralizá-lo na rolagem.
  const refHoje = useRef<HTMLDivElement>(null);
  // A caixa da rolagem lateral da grade, para voltar ao começo a cada semana.
  const rolagem = useRef<HTMLDivElement>(null);

  // A semana muda na tela E na página, para a troca de área não a perder.
  const setInicio = useCallback((proximo: (atual: Date) => Date) => {
    setInicioNaTela((atual) => {
      inicioDaPagina = proximo(atual);
      return inicioDaPagina;
    });
  }, []);
  const setSemana = useCallback((estado: EstadoDaSemana) => {
    semanaDaPagina = estado;
    setSemanaNaTela(estado);
  }, []);

  // Os leads do campo, uma leitura por login (`carregarLeadsParaCompromisso`, `admin.html:4364`),
  // pela leitura guardada `leadsDaSessao` acima, onde mora o defeito reproduzido.
  useEffect(() => {
    let vivo = true;
    void leituraDosLeads(sessao, dados).promessa.then((resultado) => {
      // Na falha o campo segue com "(nenhum)", o texto do HTML do legado antes da leitura.
      if (vivo) setLeads(resultado);
    });
    return () => {
      vivo = false;
    };
  }, [dados, sessao]);

  // A semana, relida a cada troca de semana e a cada gravação (`carregarCalendario`, `4375-4393`).
  useEffect(() => {
    let vivo = true;
    void listarCompromissosDoPeriodo(dados, inicio.toISOString(), fimDoPeriodo(inicio).toISOString()).then((resultado) => {
      if (!vivo) return;
      // A falha mostra a mensagem crua depois do prefixo, como o legado.
      setSemana(resultado.ok ? { tipo: 'pronta', inicio, compromissos: resultado.dados } : { tipo: 'erro', mensagem: resultado.erro.mensagem });
    });
    return () => {
      vivo = false;
    };
  }, [dados, inicio, recarga, setSemana]);

  // A cada desenho da semana, o dia de hoje vai para o centro da rolagem, quando está nela
  // (`admin.html:4528-4534`); é o que no celular mostra hoje em vez de domingo.
  useEffect(() => {
    if (semana.tipo !== 'pronta') return;
    // O legado RECRIA a grade a cada semana (`wrap.innerHTML`, `admin.html:4476`), e a rolagem
    // nova nasce no começo. Aqui a mesma caixa continua na tela, então ela volta ao começo à mão:
    // sem isso, a semana sem "hoje" herdava a rolagem da anterior e escondia a coluna das horas
    // (medido na homologação, tablet 768, em 19/09/2026).
    if (rolagem.current) rolagem.current.scrollLeft = 0;
    refHoje.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [semana]);

  // Abre o modal novo, vazio ou com o dia e a hora clicados (`abrirModalCompromisso`). O campo
  // "Lead" nasce no vazio, que sempre tem opção, então nunca em branco.
  const abrirNovo = useCallback((dia?: Date, hora?: number) => {
    setStatusModal('');
    setModal({ emEdicao: null, campos: camposNovos(dia, hora), statusConfirmacao: '', leadEmBranco: false });
  }, []);

  // Abre o modal de edição, preenchido (`abrirModalCompromissoEdicao`). O lead entra como o campo
  // o deixa: fora da lista de opções, ele fica em branco e valendo vazio, como no legado.
  const abrirEdicao = useCallback(
    (c: CompromissoDaSemana) => {
      setStatusModal('');
      const lead = leadNoCampo(c.lead_id, leads);
      setModal({ emEdicao: c, campos: camposDaEdicao(c, lead), statusConfirmacao: formatarStatusConfirmacao(c), leadEmBranco: lead.emBranco });
    },
    [leads],
  );

  // Fecha sem gravar: ×, "Cancelar" e ESC. A confirmação de consulta fecha junto, porque no legado
  // ela vive dentro do mesmo modal que acabou de sair da tela.
  const fecharModal = useCallback(() => {
    setModal(null);
    setConfirmandoConsulta(false);
  }, []);

  // Troca um campo do modal. Escolher no campo "Lead" tira o branco: no legado, escolher uma opção
  // é o que faz o `selectedIndex` deixar de ser -1.
  const mudar = useCallback(<K extends keyof CamposDoCompromisso>(campo: K, valor: CamposDoCompromisso[K]) => {
    setModal((atual) =>
      atual
        ? { ...atual, campos: { ...atual.campos, [campo]: valor }, leadEmBranco: campo === 'leadId' ? false : atual.leadEmBranco }
        : atual,
    );
  }, []);

  // "Salvar", passo a passo como `admin.html:4622-4697`.
  const salvar = useCallback(async () => {
    if (!modal) return;
    // A primeira frase de erro, na ordem do legado; nada vai para o banco.
    const erro = validarCompromisso(modal.campos);
    if (erro) {
      setStatusModal(erro);
      return;
    }
    // O registro, e se a cliente precisa receber o e-mail de confirmação.
    const { registro, precisaNotificarCliente } = montarRegistro(modal.campos, modal.emEdicao);
    setStatusModal('Salvando...');
    // Edição atualiza sem ler de volta; o novo devolve o `id`, que o e-mail precisa.
    let idGravado: number;
    if (modal.emEdicao) {
      const resultado = await atualizarCompromisso(dados, modal.emEdicao.id, registro);
      if (!resultado.ok) {
        setStatusModal(`Erro ao salvar: ${resultado.erro.mensagem}`);
        return;
      }
      idGravado = modal.emEdicao.id;
    } else {
      const resultado = await criarCompromisso(dados, registro);
      if (!resultado.ok) {
        setStatusModal(`Erro ao salvar: ${resultado.erro.mensagem}`);
        return;
      }
      idGravado = resultado.dados.id;
    }

    // O e-mail só sai se o lead tem e-mail, e a tela não espera por ele (`admin.html:4679-4693`).
    if (precisaNotificarCliente) {
      const lead = leads?.find((l) => String(l.id) === String(modal.campos.leadId));
      if (lead?.email) {
        void notificarConfirmacaoCompromisso(dados, {
          compromisso_id: idGravado,
          cliente_email: lead.email,
          cliente_nome: lead.nome,
          titulo: registro.titulo,
          data_hora_inicio: registro.data_hora_inicio,
          duracao_minutos: registro.duracao_minutos,
          descricao: registro.descricao,
        }).then((resultado) => {
          // O legado só registra a falha no console, sem avisar na tela (`admin.html:4691`).
          if (!resultado.ok) console.warn('E-mail de confirmação de compromisso não enviado:', resultado.erro.mensagem);
        });
      }
    }

    // Fecha e relê a semana.
    setModal(null);
    setRecarga((n) => n + 1);
  }, [modal, dados, leads]);

  // "Excluir", só na edição (`admin.html:4699-4706`).
  const excluir = useCallback(async () => {
    const emEdicao = modal?.emEdicao;
    if (!emEdicao) return;
    if (!(await confirmar('Excluir este compromisso?'))) return;
    const resultado = await excluirCompromisso(dados, emEdicao.id);
    // A falha em aviso, com a mensagem crua depois do prefixo.
    if (!resultado.ok) {
      await alertar(`Erro ao excluir: ${resultado.erro.mensagem}`);
      return;
    }
    setModal(null);
    setRecarga((n) => n + 1);
  }, [modal, dados, confirmar, alertar]);

  // "Ir para o cliente": a ficha da cliente do lead, aberta com esse lead (`admin.html:4539-4548`).
  const irParaCliente = useCallback(
    async (leadId: number) => {
      const resultado = await lerLeadDoCompromisso(dados, leadId);
      // Falha e lead inexistente dão a mesma frase, como o `error || !lead` do legado.
      if (!resultado.ok || !resultado.dados) {
        await alertar('Erro ao carregar o lead.');
        return;
      }
      const lead = resultado.dados;
      if (!lead.user_id_vinculado) {
        await alertar('Esse lead ainda não tem cliente vinculado. Veja em Vendas.');
        return;
      }
      // O `?lead=` leva o atendimento certo quando a cliente tem mais de um, e o `de=` diz à ficha
      // que o "Voltar" volta para CÁ (pedido da frente A2). No legado o "Voltar" é
      // `mostrar('tela-lista')`, e a tela de listas reaparece na aba que estava aberta, que era esta
      // (`admin.html:6158`); aqui a área desmonta, então a origem viaja na rota.
      navegar(enderecoDaFicha(lead.user_id_vinculado, { lead: lead.id, de: '/compromissos' }));
    },
    [dados, alertar, navegar],
  );

  // Erro de leitura: só a frase, e a barra some junto, como o `innerHTML` do legado (`4388`).
  if (semana.tipo === 'erro') {
    return <p className="text-cx-sm text-texto-suave">{`${t('Erro ao carregar:')} ${semana.mensagem}`}</p>;
  }
  // Antes da primeira leitura, a área fica em branco, como o `#calendario-wrap` vazio do legado.
  //
  // A grade também espera a leitura dos leads TERMINAR (`undefined` é "ainda não voltou"). No
  // legado ela termina no login, antes de a pessoa alcançar a grade; aqui ela começa ao abrir a
  // área, e sem esta espera haveria uma janela em que clicar num evento abriria o modal com o lead
  // EM BRANCO (o defeito de `leadNoCampo`) e o "Salvar" desvincularia o lead. A espera só existe na
  // primeira abertura depois do login: depois disso o resultado já está guardado na sessão.
  if (semana.tipo === 'nada' || leads === undefined) return null;

  // Os sete dias, as dezesseis horas e o dia de hoje à meia-noite, para marcar a coluna.
  const dias = Array.from({ length: NUM_DIAS }, (_, i) => somarDias(semana.inicio, i));
  const horas = horasDaGrade();
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return (
    <div>
      {/* A barra: o intervalo e os botões (`.cal-nav`, `admin.html:969-987`). */}
      <div className="flex flex-wrap items-center justify-between gap-cx-3 rounded-t-cx-cartao border border-b-0 border-borda bg-superficie-elevada px-cx-4 py-cx-3">
        <p className="text-cx-xl italic text-texto">{rotuloDoIntervalo(semana.inicio)}</p>
        {/* Abaixo de 768 px, a grade preenche a largura e o "Novo" ganha linha própria; de 768 px
            para cima, os quatro ficam lado a lado (`admin.html:982-987` e `1680-1681`). */}
        <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(104px,1fr))] gap-cx-2 md:w-auto md:grid-cols-[repeat(4,minmax(104px,auto))]">
          <Botao variante="secundario" tamanho="sm" onClick={() => setInicio((i) => somarDias(i, -NUM_DIAS))}>
            <span className="inline-flex items-center gap-cx-1">
              <IconeVoltar aria-hidden="true" className="h-cx-4 w-cx-4" />
              {t('Anterior')}
            </span>
          </Botao>
          {/* "Hoje" relê mesmo na semana atual: é uma data nova, como o `inicioDoPeriodo(new Date())`. */}
          <Botao variante="secundario" tamanho="sm" onClick={() => setInicio(() => inicioDoPeriodo(new Date()))}>
            {t('Hoje')}
          </Botao>
          <Botao variante="secundario" tamanho="sm" onClick={() => setInicio((i) => somarDias(i, NUM_DIAS))}>
            <span className="inline-flex items-center gap-cx-1">
              {t('Seguinte')}
              {/* A seta de voltar virada para o outro lado: o Chassi não tem a de avançar. */}
              <IconeVoltar aria-hidden="true" className="h-cx-4 w-cx-4 rotate-180" />
            </span>
          </Botao>
          {/* "+ Novo compromisso" some para consulta, como todo botão de ação desse papel. */}
          {!somenteLeitura && (
            <div className="col-span-full grid md:col-span-1">
              <Botao tamanho="sm" className="whitespace-nowrap" onClick={() => abrirNovo()}>
                {t('+ Novo compromisso')}
              </Botao>
            </div>
          )}
        </div>
      </div>

      {/* A grade, com rolagem lateral quando a semana não cabe (`.cal-grid-scroll`). */}
      <div ref={rolagem} data-rolagem-grade className="overflow-x-auto rounded-b-cx-cartao border border-borda bg-superficie-elevada">
        <div
          className="grid w-full"
          // Colunas de no mínimo 100 px: no celular isso liga a rolagem em vez de espremer o texto.
          style={{
            gridTemplateColumns: `${LARGURA_COLUNA_HORAS}px repeat(${NUM_DIAS}, minmax(${LARGURA_MIN_COLUNA}px, 1fr))`,
            minWidth: LARGURA_COLUNA_HORAS + NUM_DIAS * LARGURA_MIN_COLUNA,
          }}
        >
          {/* O canto vazio acima das horas. */}
          <div className="border-b border-borda" />
          {dias.map((dia, i) => {
            const ehHoje = dia.getTime() === hoje.getTime();
            return (
              <div
                key={`cabecalho-${i}`}
                ref={ehHoje ? refHoje : undefined}
                data-dia-cabecalho={i}
                data-hoje={ehHoje ? 'sim' : undefined}
                className={`border-b border-l border-borda px-cx-1 py-cx-3 text-center text-cx-xs font-semibold uppercase ${ehHoje ? 'bg-superficie-afundada text-texto' : 'text-texto-suave'}`}
              >
                {CAL_DIAS_SEMANA[dia.getDay()]}
                <span className="mt-cx-1 block text-cx-xl font-normal normal-case italic text-texto">{dia.getDate()}</span>
              </div>
            );
          })}

          {/* A coluna das horas, com o rótulo subindo 6 px até a linha (`.cal-hora-label`). */}
          <div className="relative">
            {horas.map((h) => (
              <div
                key={h}
                className="whitespace-nowrap border-t border-borda px-cx-1 text-right text-cx-xs text-texto-suave"
                style={{ height: CAL_ALTURA_HORA, transform: 'translateY(-6px)' }}
              >
                {`${String(h).padStart(2, '0')}:00`}
              </div>
            ))}
          </div>

          {dias.map((dia, i) => (
            <div key={`coluna-${i}`} className="relative border-l border-borda" style={{ height: horas.length * CAL_ALTURA_HORA }}>
              {/* Cada hora é clicável e abre "Novo compromisso" com o dia e a hora cheia daquela
                  linha, inclusive para consulta, que vê o modal travado (`admin.html:4501-4507`). */}
              {horas.map((h) => (
                <div
                  key={h}
                  data-dia-indice={i}
                  data-hora={h}
                  onClick={() => abrirNovo(dia, h)}
                  className="cursor-pointer border-t border-borda transition-colors hover:bg-superficie-afundada"
                  style={{ height: CAL_ALTURA_HORA }}
                />
              ))}
              {eventosDoDia(semana.compromissos, dia).map((ev) => {
                const c = ev.compromisso;
                return (
                  <div
                    key={c.id}
                    data-compromisso={c.id}
                    data-tom={ev.tom}
                    title={ev.dica}
                    // O clique no evento edita, e não chega à linha da hora de baixo.
                    onClick={(evento) => {
                      evento.stopPropagation();
                      abrirEdicao(c);
                    }}
                    className={`absolute cursor-pointer overflow-hidden rounded-cx-sm px-cx-2 py-cx-1 text-cx-xs leading-tight shadow-cx-sm ${COR_DO_EVENTO[ev.tom]}`}
                    // Posição pela hora e pela duração; largura dividida pelas faixas do dia.
                    style={{
                      top: ev.topo,
                      height: ev.altura,
                      left: `calc(${ev.esquerdaPct}% + 2px)`,
                      width: `calc(${ev.larguraPct}% - 4px)`,
                    }}
                  >
                    <div className="truncate font-semibold">{ev.tituloExibido}</div>
                    {/* O nome do lead abaixo, só quando o título é outro. */}
                    {c.titulo && ev.nomeLead && <div className="truncate opacity-90">{ev.nomeLead}</div>}
                    <div className="opacity-80">{ev.hora}</div>
                    {/* O WhatsApp do lead; o clique abre o WhatsApp Web e não edita (`4524-4526`).
                        A peça comum tem alvo de 44 px, maior que o próprio evento: a caixa de 16 px
                        com `overflow-hidden` recorta o alvo ao tamanho do ícone do legado, para o
                        resto do evento continuar abrindo a edição, como lá. */}
                    {c.lead_id && c.leads_funil?.contato_whatsapp && (
                      <span
                        className="absolute bottom-0 right-cx-5 flex h-4 w-4 items-center justify-center overflow-hidden"
                        onClick={(evento) => evento.stopPropagation()}
                      >
                        <LinkWhatsapp telefone={c.leads_funil.contato_whatsapp} />
                      </span>
                    )}
                    {/* "Ir para o cliente", no canto, para evento com lead (`admin.html:4460`). */}
                    {c.lead_id && (
                      <span
                        title={t('Ir para o cliente')}
                        data-ir-cliente={c.lead_id}
                        className="absolute bottom-0.5 right-cx-1 cursor-pointer leading-none"
                        onClick={(evento) => {
                          evento.stopPropagation();
                          void irParaCliente(c.lead_id as number);
                        }}
                      >
                        {/* A seta de voltar girada para cima e para a direita, no lugar do "↗". */}
                        <IconeVoltar aria-hidden="true" className="h-3 w-3 rotate-[135deg]" />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* O modal de compromisso. Montado só aberto: fechar desmonta, e o `<dialog>` sai junto. */}
      {modal && (
        <>
          {/* O modal comum do legado: × e ESC fecham, e o clique fora NÃO fecha, como o de
              compromisso do legado (`admin.html:4609-4620`). Para consulta, a peça tira o × e o
              rodapé (achado 5). */}
          <ModalLegado
            aberto
            titulo={t(modal.emEdicao ? 'Editar compromisso' : 'Novo compromisso')}
            aoFechar={fecharModal}
            fechaAoClicarFora={false}
            testid="modal-compromisso"
            rodape={
              // Consulta não tem botão nenhum aqui (achado 5); a peça já tira, e aqui nem se desenha.
              somenteLeitura ? undefined : (
                <>
                  {/* "Excluir" só existe na edição, e é vermelho por decisão do fundador. */}
                  {modal.emEdicao && (
                    <Botao variante="secundario" className={CLASSE_DE_EXCLUIR} onClick={() => void excluir()}>
                      {t('Excluir')}
                    </Botao>
                  )}
                  <Botao variante="secundario" onClick={fecharModal}>
                    {t('Cancelar')}
                  </Botao>
                  <Botao onClick={() => void salvar()}>{t('Salvar')}</Botao>
                </>
              )
            }
          >
            <div className="flex flex-col gap-cx-3">
              {/* "(selecione)" depois que os leads chegam; "(nenhum)" é o texto do HTML antes disso. */}
              <Escolha
                rotulo="Lead (opcional)"
                vazio={leads ? '(selecione)' : '(nenhum)'}
                opcoes={(leads ?? []).map((l) => ({ valor: String(l.id), rotulo: rotuloDoLead(l) }))}
                valor={modal.campos.leadId}
                aoMudar={(valor) => mudar('leadId', valor)}
                desabilitado={somenteLeitura}
                emBranco={modal.leadEmBranco}
              />
              {/* A confirmação da cliente só existe com lead (`admin.html:4553`). */}
              {modal.campos.leadId && (
                <div>
                  <label className="flex min-h-toque cursor-pointer items-start gap-cx-2 text-cx-base text-texto">
                    <input
                      type="checkbox"
                      className="mt-cx-1 h-cx-4 w-cx-4 shrink-0 accent-marca"
                      checked={modal.campos.pedirConfirmacao}
                      disabled={somenteLeitura}
                      onChange={(evento) => mudar('pedirConfirmacao', evento.target.checked)}
                    />
                    {t('Pedir confirmação da cliente (ela verá um aviso no portal pra confirmar ou recusar)')}
                  </label>
                  <p data-status-confirmacao className="mt-cx-1 text-cx-xs text-texto-suave">
                    {modal.statusConfirmacao}
                  </p>
                </div>
              )}
              <Campo
                rotulo={t('Título (opcional se selecionar um lead)')}
                placeholder={t('Ex: Reunião, follow-up, ligação...')}
                value={modal.campos.titulo}
                onChange={(evento) => mudar('titulo', evento.target.value)}
                disabled={somenteLeitura}
              />
              {/* Os quatro campos curtos em linha, como a `.linha-compacta` do legado. */}
              <div className="flex flex-wrap items-end gap-cx-3">
                <Campo
                  rotulo={t('Data')}
                  type="date"
                  value={modal.campos.data}
                  onChange={(evento) => mudar('data', evento.target.value)}
                  disabled={somenteLeitura}
                />
                <Campo
                  rotulo={t('Hora de início')}
                  type="time"
                  max="22:00"
                  value={modal.campos.hora}
                  onChange={(evento) => mudar('hora', evento.target.value)}
                  disabled={somenteLeitura}
                />
                <Escolha
                  rotulo="Duração"
                  opcoes={OPCOES_DURACAO}
                  valor={modal.campos.duracao}
                  aoMudar={(valor) => mudar('duracao', valor)}
                  desabilitado={somenteLeitura}
                />
                <Escolha
                  rotulo="Avisar por e-mail"
                  vazio="Não avisar"
                  opcoes={OPCOES_AVISO}
                  valor={modal.campos.aviso}
                  aoMudar={(valor) => mudar('aviso', valor)}
                  desabilitado={somenteLeitura}
                />
              </div>
              <CampoTexto
                rotulo={t('Observações')}
                linhas={2}
                value={modal.campos.observacoes}
                onChange={(evento) => mudar('observacoes', evento.target.value)}
                disabled={somenteLeitura}
              />
              {/* A linha de status em vermelho, como `.modal-status-erro` (`admin.html:1514`). */}
              <p data-status-modal className="text-cx-sm text-erro" role="status">
                {statusModal}
              </p>
              {/* O "Excluir" que o legado devolve a consulta na edição, no corpo porque o rodapé
                  inteiro sai para esse papel (achado 5). O defeito mora em `consulta.tsx`. */}
              {somenteLeitura && modal.emEdicao && LEGADO_REEXIBE_EXCLUIR_PARA_CONSULTA && (
                <ExcluirDeConsulta aoExcluir={() => setConfirmandoConsulta(true)} />
              )}
            </div>
          </ModalLegado>
          {/* A confirmação de consulta: "Cancelar" e nada mais, e nenhum caminho que apague. */}
          <ConfirmacaoSoComCancelar
            aberta={confirmandoConsulta}
            mensagem={t('Excluir este compromisso?')}
            aoCancelar={() => setConfirmandoConsulta(false)}
          />
        </>
      )}
      {dialogo}
    </div>
  );
}
